import { useEffect, useState } from 'react';

const DIMS = [
  { id: 'A', label: '价值引领', color: '#e879f9' },
  { id: 'B', label: '教学内容', color: '#22d3ee' },
  { id: 'C', label: '教学目标', color: '#22d3ee' },
  { id: 'D', label: '教学设计', color: '#a3e635' },
  { id: 'E', label: '组织互动', color: '#fb923c' },
  { id: 'F', label: '学生成效', color: '#fb923c' },
];

const CX = 185;
const CY = 175;
const R_GAUGE = 125;

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180.0;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(x: number, y: number, r: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(x, y, r, startAngle);
  const end = polarToCartesian(x, y, r, endAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
}

function EmptyScoreCard({ dim, align }: { dim: any; align: 'left' | 'right' }) {
  return (
    <div className="flex flex-col rounded-xl cursor-default flex-shrink-0"
      style={{
        background: `linear-gradient(145deg, rgba(255,255,255,0.03), transparent)`,
        border: `1px solid rgba(255,255,255,0.08)`,
        textAlign: align === 'left' ? 'left' : 'right',
        padding: '12px 14px',
        minWidth: '155px',
      }}>
      <div className="flex items-center gap-1.5" style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        <span style={{ fontSize: '34px', fontWeight: 900, color: 'rgba(255,255,255,0.2)', lineHeight: 1 }}>
          --
        </span>
        <span style={{ fontSize: '16px', fontWeight: 700, color: `rgba(255,255,255,0.1)`, alignSelf: 'flex-end', paddingBottom: '3px' }}>分</span>
      </div>
      <span className="font-bold whitespace-nowrap" style={{ fontSize: '18px', color: 'rgba(255,255,255,0.3)', marginTop: '6px', lineHeight: 1.2 }}>
        {dim.label}
      </span>
      <div className="mt-2.5 h-1.5 rounded-full overflow-hidden" style={{ background: `rgba(255,255,255,0.05)` }}>
      </div>
    </div>
  );
}

interface EmptyEvalCenterProps {
  onStartEval: () => void;
  isUploading?: boolean;
  canStart?: boolean;
}

export default function EmptyEvalCenter({ onStartEval, isUploading, canStart = false }: EmptyEvalCenterProps) {
  const START_ANG = 145; 
  const END_ANG = 395;
  const TOTAL_ANG = END_ANG - START_ANG;

  const leftDims = DIMS.slice(0, 3);
  const rightDims = DIMS.slice(3, 6);

  const isBtnDisabled = isUploading || !canStart;

  return (
    <div className="h-full flex flex-col" style={{ padding: '4px 8px 6px' }}>
      {/* 标题 */}
      <div className="flex items-center gap-2 px-1 mb-2 flex-shrink-0">
        <div className="w-1 h-4 rounded-full flex-shrink-0"
          style={{ background: 'linear-gradient(to bottom, #22d3ee, rgba(34,211,238,0.5))', boxShadow: '0 0 10px rgba(34,211,238,0.9)' }} />
        <span className="text-white font-bold font-semibold tracking-wide" style={{ fontSize: '24px' }}>AI综合评分</span>
        <span className="ml-2 px-2 py-0.5 rounded-full text-base"
          style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24' }}>
          待评价
        </span>
      </div>

      <div className="flex items-center flex-1 min-h-0 gap-2">
        {/* 左侧弧形列 */}
        <div className="flex flex-col justify-around h-full flex-shrink-0 py-2" style={{ width: '150px', zIndex: 10 }}>
          {leftDims.map((d, index) => {
            const xOffset = index === 1 ? 65 : 125;
            return (
              <div key={d.id} className="transition-transform duration-500" style={{ transform: `translateX(${xOffset}px)` }}>
                <EmptyScoreCard dim={d} align="left" />
              </div>
            );
          })}
        </div>

        {/* 核心仪表盘骨架 */}
        <div className="flex-1 flex flex-col items-center justify-center min-w-0 relative h-full">
          <svg
            viewBox={`0 0 ${CX * 2} ${CY * 2.1}`}
            style={{ width: '100%', height: '100%', maxWidth: '390px', maxHeight: '390px', overflow: 'visible', margin: '0 auto' }}
          >
            {/* 背景暗轨 */}
            <path
              d={describeArc(CX, CY, R_GAUGE, START_ANG, END_ANG)}
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="16"
              strokeLinecap="round"
            />

            {/* 仅绘制全灰的不活跃刻度 */}
            {Array.from({ length: 51 }).map((_, i) => {
              const a = START_ANG + i * (TOTAL_ANG / 50);
              const isMajor = i % 2 === 0;
              const innerR = isMajor ? R_GAUGE - 24 : R_GAUGE - 18;
              const pt1 = polarToCartesian(CX, CY, innerR, a);
              const pt2 = polarToCartesian(CX, CY, R_GAUGE - 12, a);
              
              return (
                <line key={i} x1={pt1.x} y1={pt1.y} x2={pt2.x} y2={pt2.y}
                  stroke={"rgba(255,255,255,0.05)"}
                  strokeWidth={isMajor ? "2.5" : "1.2"} />
              );
            })}
          </svg>

          {/* 图表标题和缺省数据提示 */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pt-8 pointer-events-none">
            <span style={{ fontSize: '46px', fontWeight: 900, color: 'rgba(255,255,255,0.15)', fontFamily: 'monospace', lineHeight: 1 }}>
              --
            </span>
            <span style={{ fontSize: '24px', fontWeight: 700, color: 'rgba(255,255,255,0.3)', marginTop: '8px', letterSpacing: '0.1em' }}>
              综合评分
            </span>
          </div>
        </div>

        {/* 右侧弧形列 */}
        <div className="flex flex-col justify-around h-full flex-shrink-0 py-2" style={{ width: '150px', zIndex: 10 }}>
          {rightDims.map((d, index) => {
            const xOffset = index === 1 ? -65 : -125;
            return (
              <div key={d.id} className="transition-transform duration-500" style={{ transform: `translateX(${xOffset}px)` }}>
                <EmptyScoreCard dim={d} align="right" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
